import { T } from '@transifex/react';
import { cn, Divider, Heading, Skeleton, Text, VStack } from '@vidahealth/vida-ui';
import { Pressable, useWindowDimensions, View } from 'react-native';

import { CoachImage } from '@/components/CoachImage';
import { coachName } from '@/components/CoachSummary';
import type { MatchedCoach } from '@/modules/api';

import { NATIVE_CARD_SHADOW } from '../constants/cardShadow';
import { useNextAppointmentLabel } from '../hooks/useNextAppointmentLabel';

import { BestFitPill } from './BestFitPill';

const MD_BREAKPOINT = 520;

/**
 * Private to CoachMatchingScreen — the hero "Best fit" coach card. Two-column at
 * md+, vertical below. Mirrors the vida-design-system demo.
 */
export function RecommendedCoachCard({
  coach,
  selected,
  onPress,
  onReadBio,
}: {
  coach: MatchedCoach;
  selected: boolean;
  onPress: () => void;
  onReadBio: () => void;
}) {
  const { width } = useWindowDimensions();
  const isMd = width >= MD_BREAKPOINT;
  const { label: nextAppointment, isLoading: loadingAppointment } = useNextAppointmentLabel(
    coach.userUuid
  );

  return (
    <View
      style={[NATIVE_CARD_SHADOW, !isMd ? { paddingTop: 16 } : null]}
      className='relative rounded-lg shadow-hard-5'
    >
      <View
        className={cn(
          'overflow-hidden rounded-lg border border-outline-50',
          selected ? 'border-2 border-primary-500' : ''
        )}
      >
        <Pressable
          accessibilityRole='button'
          accessibilityState={{ selected }}
          onPress={onPress}
          testID={`recommended-${coach.uuid}`}
          className={cn(
            'flex bg-background-0 dark:bg-background-50',
            isMd ? 'flex-row' : 'flex-col'
          )}
        >
          <CoachImage
            coach={coach}
            // Two-column (md): fill a stretched box, floored so the "Best fit"
            // image stays prominent when the text is short (single-line name, no
            // next-appointment row). Stacked: fit the photo's natural ratio.
            sizing={isMd ? 'fill' : 'fit'}
            containerStyle={
              isMd ? { width: '50%', alignSelf: 'stretch', minHeight: 300 } : { width: '100%' }
            }
            showTopCoach
            badgePosition={isMd ? 'center' : 'right'}
          />
          <View style={isMd ? { flex: 1 } : undefined}>
            <VStack space='lg' className='px-6 py-4'>
              {isMd ? <BestFitPill /> : null}
              <VStack space='xs'>
                <Heading size='xl' className='font-poppins-600 text-typography-900'>
                  {coachName(coach)}
                </Heading>
                {coach.title ? (
                  <Text size='md' className='font-body text-typography-900'>
                    {coach.title}
                  </Text>
                ) : null}
              </VStack>
              {coach.bio ? (
                <>
                  <Divider />
                  <VStack space='sm'>
                    <Text size='sm' className='font-body text-typography-900' numberOfLines={2}>
                      {coach.bio}
                    </Text>
                    <Pressable
                      onPress={onReadBio}
                      testID={`recommended-${coach.uuid}-read-bio`}
                      className='self-start'
                    >
                      <Text size='sm' className='text-primary-500' weight='medium'>
                        <T _str='Read full bio' />
                      </Text>
                    </Pressable>
                  </VStack>
                </>
              ) : null}
              {loadingAppointment || nextAppointment ? (
                <>
                  <Divider />
                  <VStack space='xs'>
                    <Text size='sm' weight='semibold' className='text-typography-900'>
                      <T _str='Next appointment' />
                    </Text>
                    {loadingAppointment ? (
                      <Skeleton variant='rounded' className='h-4 w-3/4' />
                    ) : (
                      <Text size='sm' className='font-body text-typography-600' numberOfLines={1}>
                        {nextAppointment}
                      </Text>
                    )}
                  </VStack>
                </>
              ) : null}
            </VStack>
          </View>
        </Pressable>
      </View>

      {!isMd ? (
        <View
          style={{ position: 'absolute', top: 0, left: 0, right: 0, alignItems: 'center' }}
          pointerEvents='box-none'
        >
          <BestFitPill />
        </View>
      ) : null}
    </View>
  );
}
